import { useMutation } from "@apollo/client/react";
import { ClientProfileInput, PrivateClientProfileInput, WorkerProfileInput } from "../__generated__/graphql";
import { CreateClientProfileMutation, CreatePrivateClientProfileMutation, CreateWorkerProfileMutation } from "../__generated__/operations/graphql";
import { useCallback, use } from "react";
import { AuthManagerContext, AuthStatus } from "../contexts/AuthManager";
import { useI18n } from "./i18n-hooks";
import { useSnackbar } from "./snackbar";
import { useTracking } from "./tracking";
import {
    CREATE_CLIENT_PROFILE,
    CREATE_PRIVATE_CLIENT_PROFILE,
    CREATE_WORKER,
} from "../graphql/core";

export enum SignupProfileType {
    CLIENT = "client",
    PRIVATE_CLIENT = "private_client",
    WORKER = "worker",
}

type SignupData = {
    client: ClientProfileInput;
    private_client: PrivateClientProfileInput;
    worker: WorkerProfileInput;
};

type MutationType = {
    client: CreateClientProfileMutation;
    private_client: CreatePrivateClientProfileMutation;
    worker: CreateWorkerProfileMutation;
};

export const useSignup = <T extends SignupProfileType>(signupProfileType: T) => {
    const { status, refreshUser } = use(AuthManagerContext);
    const i18n = useI18n();
    const { showSnackbar } = useSnackbar();
    const { trackEvent } = useTracking();

    const [initUser, { loading: initLoading, error: initError }] = useMutation<MutationType[T]>(
        (() => {
            switch (signupProfileType) {
                case SignupProfileType.CLIENT:
                    return CREATE_CLIENT_PROFILE;
                case SignupProfileType.PRIVATE_CLIENT:
                    return CREATE_PRIVATE_CLIENT_PROFILE;
                case SignupProfileType.WORKER:
                    return CREATE_WORKER;
                default:
                    throw new Error(`Unknown signupProfileType: ${signupProfileType}`);
            }
        })(),
    );

    const loading = initLoading;
    const error = initError;

    const signup = useCallback(
        async (signupData: SignupData[T]) => {
            if (
                !signupData ||
                (signupProfileType === SignupProfileType.WORKER &&
                    !(signupData as WorkerProfileInput)?.user?.email) ||
                ((signupProfileType === SignupProfileType.CLIENT ||
                    signupProfileType === SignupProfileType.PRIVATE_CLIENT) &&
                    !(signupData as ClientProfileInput)?.businessEmail)
            ) {
                showSnackbar({
                    mode: "error",
                    text: i18n.t("screens.auth.signup.incomplete_data"),
                });
                return false;
            }

            let result;

            try {
                if (status === AuthStatus.Out) {
                    const variables = (() => {
                        switch (signupProfileType) {
                            case SignupProfileType.CLIENT:
                                return { clientProfile: signupData };
                            case SignupProfileType.PRIVATE_CLIENT:
                                return { privateClientProfile: signupData };
                            case SignupProfileType.WORKER:
                                return { workerProfile: signupData };
                        }
                    })();

                    result = await initUser({ variables });
                } else {
                    // no need to create a user, you are already logged in
                    result = { errors: null };
                }

                if (!result.error) {
                    // this will automatically change the navigations as user will become completed and a
                    // new stack of screen will be loaded
                    void trackEvent("pre_signup_completed", { profile_type: signupProfileType });
                    await refreshUser?.();
                    return true;
                }
            } catch (_e) {
                /* empty */
            }
            showSnackbar({
                mode: "error",
                text: i18n.t("screens.auth.signup.unknown_error"),
            });
            return false;
        },
        [showSnackbar, i18n, status, signupProfileType, initUser, refreshUser, trackEvent],
    );

    return { signup, loading, error };
};
